'use client';

import { useState, useEffect } from 'react';
import { 
  Code, 
  Plus, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Archive,
  Save,
  Loader2,
  Link2,
  X,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  useTechnicalSpecs,
  useCreateTechnicalSpec,
  useUpdateTechnicalSpec,
  useUpdateTechnicalSpecStatus,
  useDeleteTechnicalSpec,
  type TechnicalSpec,
} from '@/hooks/use-technical-specs';
import { useContracts } from '@/hooks/use-contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface TechnicalSpecEditorProps {
  projectId: string;
  projectName?: string;
}

const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Code },
  IN_REVIEW: { label: 'In Review', color: 'bg-blue-100 text-blue-800', icon: Clock },
  APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  ARCHIVED: { label: 'Archived', color: 'bg-gray-100 text-gray-600', icon: Archive },
};

const VALID_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['IN_REVIEW'],
  IN_REVIEW: ['APPROVED', 'DRAFT'],
  APPROVED: ['ARCHIVED', 'DRAFT'],
  ARCHIVED: ['DRAFT'],
};

export function TechnicalSpecEditor({ projectId, projectName = 'Project' }: TechnicalSpecEditorProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingSpec, setEditingSpec] = useState<TechnicalSpec | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  const { toast } = useToast();
  const { data: technicalSpecs, isLoading } = useTechnicalSpecs(projectId);
  const createMutation = useCreateTechnicalSpec(projectId);
  const updateMutation = useUpdateTechnicalSpec(projectId);
  const statusMutation = useUpdateTechnicalSpecStatus(projectId);
  const deleteMutation = useDeleteTechnicalSpec(projectId);

  const handleCreate = async (data: Partial<TechnicalSpec>) => {
    try {
      await createMutation.mutateAsync(data);
      toast({ title: 'Technical Spec created', description: 'Technical spec has been created successfully.' });
      setShowCreateDialog(false);
    } catch (error) {
      toast({ 
        title: 'Creation failed', 
        description: error instanceof Error ? error.message : 'Failed to create technical spec',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (specId: string, data: Partial<TechnicalSpec>) => {
    try {
      await updateMutation.mutateAsync({ specId, data });
      toast({ title: 'Technical Spec updated', description: 'Technical spec has been updated successfully.' });
      setEditingSpec(null);
    } catch (error) {
      toast({ 
        title: 'Update failed', 
        description: error instanceof Error ? error.message : 'Failed to update technical spec',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (specId: string, newStatus: TechnicalSpec['status']) => {
    try {
      await statusMutation.mutateAsync({ specId, status: newStatus });
      toast({ title: 'Status updated', description: `Technical spec status changed to ${newStatus}.` });
    } catch (error) {
      toast({ 
        title: 'Status update failed', 
        description: error instanceof Error ? error.message : 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (specId: string) => {
    try {
      await deleteMutation.mutateAsync(specId);
      toast({ title: 'Technical Spec deleted', description: 'Technical spec has been deleted successfully.' });
      setShowDeleteConfirm(null);
    } catch (error) {
      toast({ 
        title: 'Deletion failed', 
        description: error instanceof Error ? error.message : 'Failed to delete technical spec',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Technical Specs</h1>
            <p className="text-sm text-gray-600 mt-1">Manage technical specifications for {projectName}</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Technical Spec
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!technicalSpecs || technicalSpecs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Code className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Technical Specs</h3>
            <p className="text-gray-600 mb-6">Create your first technical spec to get started.</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Technical Spec
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {technicalSpecs.map((spec) => {
              const statusConfig = STATUS_CONFIG[spec.status];
              const StatusIcon = statusConfig.icon;
              const availableTransitions = VALID_TRANSITIONS[spec.status] || [];
              
              return (
                <Card key={spec.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg">{spec.title}</CardTitle>
                          <Badge className={cn(statusConfig.color, 'flex items-center gap-1')}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <CardDescription>Version {spec.version}</CardDescription>
                        {spec.description && (
                          <p className="text-sm text-gray-600 mt-2">{spec.description}</p>
                        )}
                        {spec.linkedContract && (
                          <div className="flex items-center gap-2 mt-2">
                            <Link2 className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              Linked to: {spec.linkedContract.title} (v{spec.linkedContract.version})
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingSpec(spec)}
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(spec.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {availableTransitions.length > 0 && (
                      <div className="flex items-center gap-2 pt-2 border-t">
                        <span className="text-sm text-gray-600">Change status:</span>
                        {availableTransitions.map((transition) => (
                          <Button
                            key={transition}
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusChange(spec.id, transition as TechnicalSpec['status'])}
                            disabled={statusMutation.isPending}
                          >
                            {STATUS_CONFIG[transition as keyof typeof STATUS_CONFIG].label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <TechnicalSpecDialog
        isOpen={showCreateDialog || editingSpec !== null}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingSpec(null);
        }}
        onSave={editingSpec 
          ? (data) => handleUpdate(editingSpec.id, data)
          : handleCreate
        }
        spec={editingSpec}
        isLoading={createMutation.isPending || updateMutation.isPending}
        projectId={projectId}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Technical Spec</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this technical spec? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface TechnicalSpecDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<TechnicalSpec>) => Promise<void>;
  spec?: TechnicalSpec | null;
  isLoading?: boolean;
  projectId: string;
}

// Type definitions for structured JSON fields
type ArchitectureDecision = {
  decision: string;
  context: string;
  consequences: string[];
  status: 'proposed' | 'accepted' | 'rejected';
};

type Component = {
  name: string;
  type: string;
  description?: string;
  dependencies?: string[];
  interfaces?: string[];
};

type DataModel = {
  name: string;
  fields: Array<{
    name: string;
    type: string;
    nullable: boolean;
    description?: string;
  }>;
  relationships?: Array<{
    target: string;
    type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  }>;
};

type SecurityRequirement = {
  category: string;
  requirement: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  implementation?: string;
};

type PerformanceRequirement = {
  metric: string;
  target: string;
  condition?: string;
  measurement?: string;
};

function TechnicalSpecDialog({ isOpen, onClose, onSave, spec, isLoading, projectId }: TechnicalSpecDialogProps) {
  const { data: contracts } = useContracts(projectId);
  const [contractSearch, setContractSearch] = useState('');
  const [selectedContractId, setSelectedContractId] = useState<string | undefined>(spec?.contractId || undefined);

  // Helper to safely parse JSON fields
  const parseJsonField = <T,>(value: Record<string, unknown> | null | undefined, defaultValue: T[]): T[] => {
    if (!value) return defaultValue;
    if (Array.isArray(value)) return value as T[];
    return defaultValue;
  };

  const [formData, setFormData] = useState<Partial<TechnicalSpec>>({
    title: spec?.title || '',
    version: spec?.version || '1.0.0',
    description: spec?.description || '',
    contractId: spec?.contractId || undefined,
    architecture: spec?.architecture || null,
    components: spec?.components || null,
    dataModels: spec?.dataModels || null,
    securityReqs: spec?.securityReqs || null,
    performanceReqs: spec?.performanceReqs || null,
  });

  // Local state for structured fields
  const [architectureDecisions, setArchitectureDecisions] = useState<ArchitectureDecision[]>(() => {
    const arch = spec?.architecture as Record<string, unknown> | null;
    if (arch && Array.isArray(arch.decisions)) {
      return arch.decisions as ArchitectureDecision[];
    }
    return [];
  });

  const [components, setComponents] = useState<Component[]>(() => {
    const comps = spec?.components as Record<string, unknown> | null;
    if (comps && Array.isArray(comps.items)) {
      return comps.items as Component[];
    }
    return [];
  });

  const [dataModels, setDataModels] = useState<DataModel[]>(() => {
    const models = spec?.dataModels as Record<string, unknown> | null;
    if (models && Array.isArray(models.entities)) {
      return models.entities as DataModel[];
    }
    return [];
  });

  const [securityReqs, setSecurityReqs] = useState<SecurityRequirement[]>(() => {
    const sec = spec?.securityReqs as Record<string, unknown> | null;
    if (sec && Array.isArray(sec.requirements)) {
      return sec.requirements as SecurityRequirement[];
    }
    return [];
  });

  const [performanceReqs, setPerformanceReqs] = useState<PerformanceRequirement[]>(() => {
    const perf = spec?.performanceReqs as Record<string, unknown> | null;
    if (perf && Array.isArray(perf.requirements)) {
      return perf.requirements as PerformanceRequirement[];
    }
    return [];
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: spec?.title || '',
        version: spec?.version || '1.0.0',
        description: spec?.description || '',
        contractId: spec?.contractId || undefined,
        architecture: spec?.architecture || null,
        components: spec?.components || null,
        dataModels: spec?.dataModels || null,
        securityReqs: spec?.securityReqs || null,
        performanceReqs: spec?.performanceReqs || null,
      });

      const arch = spec?.architecture as Record<string, unknown> | null;
      setArchitectureDecisions(arch && Array.isArray(arch.decisions) 
        ? (arch.decisions as ArchitectureDecision[]) 
        : []);

      const comps = spec?.components as Record<string, unknown> | null;
      setComponents(comps && Array.isArray(comps.items) 
        ? (comps.items as Component[]) 
        : []);

      const models = spec?.dataModels as Record<string, unknown> | null;
      setDataModels(models && Array.isArray(models.entities) 
        ? (models.entities as DataModel[]) 
        : []);

      const sec = spec?.securityReqs as Record<string, unknown> | null;
      setSecurityReqs(sec && Array.isArray(sec.requirements) 
        ? (sec.requirements as SecurityRequirement[]) 
        : []);

      const perf = spec?.performanceReqs as Record<string, unknown> | null;
      setPerformanceReqs(perf && Array.isArray(perf.requirements) 
        ? (perf.requirements as PerformanceRequirement[]) 
        : []);

      setSelectedContractId(spec?.contractId || undefined);
      setContractSearch('');
    }
  }, [isOpen, spec]);

  // Filter contracts by search
  const filteredContracts = contracts?.filter(c => 
    c.title.toLowerCase().includes(contractSearch.toLowerCase()) ||
    c.version.toLowerCase().includes(contractSearch.toLowerCase())
  ) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const dataToSave: Partial<TechnicalSpec> = {
      ...formData,
      contractId: selectedContractId || null,
      architecture: architectureDecisions.length > 0 ? { decisions: architectureDecisions } : null,
      components: components.length > 0 ? { items: components } : null,
      dataModels: dataModels.length > 0 ? { entities: dataModels } : null,
      securityReqs: securityReqs.length > 0 ? { requirements: securityReqs } : null,
      performanceReqs: performanceReqs.length > 0 ? { requirements: performanceReqs } : null,
    };
    
    await onSave(dataToSave);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{spec ? 'Edit Technical Spec' : 'Create Technical Spec'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Version</label>
            <Input
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Contract Linking */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Linked Contract</label>
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search contracts..."
                  value={contractSearch}
                  onChange={(e) => setContractSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={selectedContractId || ''}
                onValueChange={(value) => setSelectedContractId(value || undefined)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a contract (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {filteredContracts.map((contract) => (
                    <SelectItem key={contract.id} value={contract.id}>
                      {contract.title} (v{contract.version})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedContractId && (
                <div className="flex items-center gap-2 pt-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    {contracts?.find(c => c.id === selectedContractId)?.title}
                    <button
                      type="button"
                      onClick={() => setSelectedContractId(undefined)}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Architecture Decisions Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Architecture Decisions</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setArchitectureDecisions([...architectureDecisions, { decision: '', context: '', consequences: [], status: 'proposed' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Decision
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {architectureDecisions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No architecture decisions. Click "Add Decision" to add one.</p>
              ) : (
                architectureDecisions.map((decision, index) => (
                  <div key={index} className="p-3 border rounded bg-gray-50 space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Decision"
                          value={decision.decision}
                          onChange={(e) => {
                            const updated = [...architectureDecisions];
                            updated[index] = { ...updated[index], decision: e.target.value };
                            setArchitectureDecisions(updated);
                          }}
                        />
                        <Textarea
                          placeholder="Context"
                          value={decision.context}
                          onChange={(e) => {
                            const updated = [...architectureDecisions];
                            updated[index] = { ...updated[index], context: e.target.value };
                            setArchitectureDecisions(updated);
                          }}
                          rows={2}
                          className="text-sm"
                        />
                        <div className="flex gap-2 items-center">
                          <Select
                            value={decision.status}
                            onValueChange={(value: ArchitectureDecision['status']) => {
                              const updated = [...architectureDecisions];
                              updated[index] = { ...updated[index], status: value };
                              setArchitectureDecisions(updated);
                            }}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="proposed">Proposed</SelectItem>
                              <SelectItem value="accepted">Accepted</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs font-medium text-gray-600">Consequences</label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = [...architectureDecisions];
                                  updated[index] = {
                                    ...updated[index],
                                    consequences: [...(updated[index].consequences || []), ''],
                                  };
                                  setArchitectureDecisions(updated);
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            </div>
                            <div className="space-y-1">
                              {(decision.consequences || []).map((consequence, consIndex) => (
                                <div key={consIndex} className="flex gap-1">
                                  <Input
                                    placeholder="Consequence"
                                    value={consequence}
                                    onChange={(e) => {
                                      const updated = [...architectureDecisions];
                                      const consequences = [...(updated[index].consequences || [])];
                                      consequences[consIndex] = e.target.value;
                                      updated[index] = { ...updated[index], consequences };
                                      setArchitectureDecisions(updated);
                                    }}
                                    className="flex-1 text-sm"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const updated = [...architectureDecisions];
                                      const consequences = (updated[index].consequences || []).filter((_, i) => i !== consIndex);
                                      updated[index] = { ...updated[index], consequences };
                                      setArchitectureDecisions(updated);
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setArchitectureDecisions(architectureDecisions.filter((_, i) => i !== index))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Components Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Components</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setComponents([...components, { name: '', type: '' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Component
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {components.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No components. Click "Add Component" to add one.</p>
              ) : (
                components.map((component, index) => (
                  <div key={index} className="p-3 border rounded bg-gray-50 space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Component name"
                            value={component.name}
                            onChange={(e) => {
                              const updated = [...components];
                              updated[index] = { ...updated[index], name: e.target.value };
                              setComponents(updated);
                            }}
                            className="flex-1"
                          />
                          <Input
                            placeholder="Type"
                            value={component.type}
                            onChange={(e) => {
                              const updated = [...components];
                              updated[index] = { ...updated[index], type: e.target.value };
                              setComponents(updated);
                            }}
                            className="w-32"
                          />
                        </div>
                        <Textarea
                          placeholder="Description (optional)"
                          value={component.description || ''}
                          onChange={(e) => {
                            const updated = [...components];
                            updated[index] = { ...updated[index], description: e.target.value };
                            setComponents(updated);
                          }}
                          rows={2}
                          className="text-sm"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setComponents(components.filter((_, i) => i !== index))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Data Models Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Data Models</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDataModels([...dataModels, { name: '', fields: [] }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Model
              </Button>
            </div>
            <div className="space-y-3 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {dataModels.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No data models. Click "Add Model" to add one.</p>
              ) : (
                dataModels.map((model, modelIndex) => (
                  <div key={modelIndex} className="p-3 border rounded bg-gray-50 space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Model name"
                          value={model.name}
                          onChange={(e) => {
                            const updated = [...dataModels];
                            updated[modelIndex] = { ...updated[modelIndex], name: e.target.value };
                            setDataModels(updated);
                          }}
                        />
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-600">Fields</label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = [...dataModels];
                                updated[modelIndex] = {
                                  ...updated[modelIndex],
                                  fields: [...(updated[modelIndex].fields || []), { name: '', type: 'string', nullable: false }],
                                };
                                setDataModels(updated);
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Field
                            </Button>
                          </div>
                          {(model.fields || []).map((field, fieldIndex) => (
                            <div key={fieldIndex} className="flex gap-2 items-center">
                              <Input
                                placeholder="Field name"
                                value={field.name}
                                onChange={(e) => {
                                  const updated = [...dataModels];
                                  const fields = [...(updated[modelIndex].fields || [])];
                                  fields[fieldIndex] = { ...fields[fieldIndex], name: e.target.value };
                                  updated[modelIndex] = { ...updated[modelIndex], fields };
                                  setDataModels(updated);
                                }}
                                className="flex-1"
                              />
                              <Input
                                placeholder="Type"
                                value={field.type}
                                onChange={(e) => {
                                  const updated = [...dataModels];
                                  const fields = [...(updated[modelIndex].fields || [])];
                                  fields[fieldIndex] = { ...fields[fieldIndex], type: e.target.value };
                                  updated[modelIndex] = { ...updated[modelIndex], fields };
                                  setDataModels(updated);
                                }}
                                className="w-24"
                              />
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={field.nullable}
                                  onChange={(e) => {
                                    const updated = [...dataModels];
                                    const fields = [...(updated[modelIndex].fields || [])];
                                    fields[fieldIndex] = { ...fields[fieldIndex], nullable: e.target.checked };
                                    updated[modelIndex] = { ...updated[modelIndex], fields };
                                    setDataModels(updated);
                                  }}
                                />
                                Nullable
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = [...dataModels];
                                  const fields = (updated[modelIndex].fields || []).filter((_, i) => i !== fieldIndex);
                                  updated[modelIndex] = { ...updated[modelIndex], fields };
                                  setDataModels(updated);
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDataModels(dataModels.filter((_, i) => i !== modelIndex))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Security Requirements Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Security Requirements</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSecurityReqs([...securityReqs, { category: '', requirement: '', priority: 'medium' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Requirement
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {securityReqs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No security requirements. Click "Add Requirement" to add one.</p>
              ) : (
                securityReqs.map((req, index) => (
                  <div key={index} className="flex gap-2 items-start p-2 border rounded bg-gray-50">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Category (e.g., Authentication)"
                        value={req.category}
                        onChange={(e) => {
                          const updated = [...securityReqs];
                          updated[index] = { ...updated[index], category: e.target.value };
                          setSecurityReqs(updated);
                        }}
                      />
                      <Textarea
                        placeholder="Requirement description"
                        value={req.requirement}
                        onChange={(e) => {
                          const updated = [...securityReqs];
                          updated[index] = { ...updated[index], requirement: e.target.value };
                          setSecurityReqs(updated);
                        }}
                        rows={2}
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Select
                          value={req.priority}
                          onValueChange={(value: SecurityRequirement['priority']) => {
                            const updated = [...securityReqs];
                            updated[index] = { ...updated[index], priority: value };
                            setSecurityReqs(updated);
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Implementation notes (optional)"
                          value={req.implementation || ''}
                          onChange={(e) => {
                            const updated = [...securityReqs];
                            updated[index] = { ...updated[index], implementation: e.target.value };
                            setSecurityReqs(updated);
                          }}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSecurityReqs(securityReqs.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Performance Requirements Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Performance Requirements</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPerformanceReqs([...performanceReqs, { metric: '', target: '' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Requirement
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {performanceReqs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No performance requirements. Click "Add Requirement" to add one.</p>
              ) : (
                performanceReqs.map((req, index) => (
                  <div key={index} className="flex gap-2 items-start p-2 border rounded bg-gray-50">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Metric (e.g., Response Time)"
                        value={req.metric}
                        onChange={(e) => {
                          const updated = [...performanceReqs];
                          updated[index] = { ...updated[index], metric: e.target.value };
                          setPerformanceReqs(updated);
                        }}
                      />
                      <div className="flex gap-2">
                        <Input
                          placeholder="Target value"
                          value={req.target}
                          onChange={(e) => {
                            const updated = [...performanceReqs];
                            updated[index] = { ...updated[index], target: e.target.value };
                            setPerformanceReqs(updated);
                          }}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Condition (optional)"
                          value={req.condition || ''}
                          onChange={(e) => {
                            const updated = [...performanceReqs];
                            updated[index] = { ...updated[index], condition: e.target.value };
                            setPerformanceReqs(updated);
                          }}
                          className="flex-1"
                        />
                      </div>
                      <Input
                        placeholder="Measurement method (optional)"
                        value={req.measurement || ''}
                        onChange={(e) => {
                          const updated = [...performanceReqs];
                          updated[index] = { ...updated[index], measurement: e.target.value };
                          setPerformanceReqs(updated);
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPerformanceReqs(performanceReqs.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
